import { BreadcrumbItemProps, Breadcrumbs, Image, Typography } from '@gyym/ui'
import { ImageProps } from 'next/image'
import React from 'react'

import { BaseLayout, BaseLayoutProps } from '../BaseLayout'
import {
  BreadcrumbsContainer,
  Container,
  Content,
  CoverContainer,
  HeadingContainer,
} from './styles'

const SINGLE_COL_MAX_WIDTH = 764 as const
// TODO: Set max width to 1024
// const SINGLE_COL_MAX_WIDTH = 1024 as const

export interface SingleColumnLayoutProps extends BaseLayoutProps {
  maxWidth?: number
  heading?: string
  subheading?: string
  paragraph?: string | React.ReactNode
  breadcrumbs?: {
    label: string
    href: BreadcrumbItemProps['href']
    as?: BreadcrumbItemProps['as']
  }[]
  breadcrumbsGrayBackground?: boolean
  hideTabletHeading?: boolean
  hideTabletBreadcrumbs?: boolean
  coverImg?: {
    src: ImageProps['src']
    alt: ImageProps['alt']
    objectPosition?: string | number
  }
  paddingX?: number | [number, number]
  /** コンテナ (max-width / padding) の外側・フッターの直上に置くコンテンツ (全幅バナーなど) */
  bottomContent?: React.ReactNode
}

export const SingleColumnLayout = ({
  maxWidth = SINGLE_COL_MAX_WIDTH,
  heading,
  subheading,
  paragraph,
  breadcrumbs,
  breadcrumbsGrayBackground,
  hideTabletHeading = false,
  hideTabletBreadcrumbs = false,
  coverImg,
  paddingX = [4, 10],
  bottomContent,
  children,
  ...baseLayoutProps
}: SingleColumnLayoutProps) => {
  return (
    <BaseLayout {...baseLayoutProps}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <BreadcrumbsContainer
          maxWidth={maxWidth}
          paddingX={paddingX}
          hideFromTablet={hideTabletBreadcrumbs}
          grayBackground={breadcrumbsGrayBackground}
        >
          <Breadcrumbs>
            {breadcrumbs.map((breadcrumb, i) => (
              <Breadcrumbs.Item
                key={i}
                href={breadcrumb.href}
                as={breadcrumb.as}
              >
                {breadcrumb.label}
              </Breadcrumbs.Item>
            ))}
          </Breadcrumbs>
        </BreadcrumbsContainer>
      )}

      {coverImg && (
        <CoverContainer>
          <Image
            src={coverImg.src}
            alt={coverImg.alt}
            fill
            style={{
              objectFit: 'cover',
              objectPosition: coverImg.objectPosition ?? 'center',
            }}
            priority
          />
        </CoverContainer>
      )}

      {heading && (
        <>
          <HeadingContainer
            maxWidth={maxWidth}
            paddingX={paddingX}
            hideFromTablet={hideTabletHeading}
          >
            <Typography variant="headline1" as="h1">
              {heading}
            </Typography>
            {subheading && (
              <Typography
                variant="subhead1"
                as="div"
                ml={['auto', 0]}
                pl={[4, 0]}
              >
                {subheading}
              </Typography>
            )}
            {paragraph && (
              <div className="flex w-full">
                <Typography variant="body1" as="p" mt={4} color="gray.01">
                  {paragraph}
                </Typography>
              </div>
            )}
          </HeadingContainer>
        </>
      )}

      <Container hasCover={!!coverImg} maxWidth={maxWidth} paddingX={paddingX}>
        <Content hasHeading={!!heading}>{children}</Content>
      </Container>

      {bottomContent}
    </BaseLayout>
  )
}
